<script setup lang="ts">
import {
	computed,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	onUnmounted,
	provide,
	shallowRef,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useBaseStore } from "@/stores/base";
import useDialog from "@/ui/composables/useDialog";
import { useBasePriceBrutoStore } from "@/modules/base_price/brutoStore";
import { versionsDrawerSourceKey } from "@/modules/base_price/versionsDrawerSource";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import BrutoHierarchySidebar from "@/modules/base_price/components/BrutoHierarchySidebar.vue";
import BrutoCpmPanel from "@/modules/base_price/components/BrutoCpmPanel.vue";
import PlanVersionsDrawer from "@/modules/base_price/components/PlanVersionsDrawer.vue";

/**
 * The bruto counterpart of BasePricePlanEditor: the same full-screen shell,
 * versions drawer and save-as-new-version flow, but nothing to configure except
 * a CPM per level (or screen) per month. No target, no books, no impressions.
 */
const { t } = useI18n();
const router = useRouter();
const store = useBasePriceBrutoStore();
const plan_uuid: string = (useRoute().params.plan_uuid ?? "").toString();

const editPath = (uuid: string) => `/base-prices/${uuid}/bruto`;

// The versions drawer is the netto editor's; this hands it the bruto store and
// where a bruto version is edited.
provide(versionsDrawerSourceKey, {
	store,
	editPath,
	showBudget: false,
});

const breadcrumbs = computed(() => [
	{
		title: t("base_price.overview.title"),
		disabled: false,
		to: "/base-prices?pricing_model=bruto",
	},
	{
		title: store.plan?.name ?? t("base_price.bruto.editor.breadcrumb"),
		disabled: true,
		to: "",
	},
]);

onBeforeMount(() => (useBaseStore().appFullWidth = true));
onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
	store.$reset();
});

/** The drawer flags are shared with the app shell — see BasePricePlanEditor. */
onUnmounted(() => {
	const baseStore = useBaseStore();

	baseStore.showSecondaryDrawer = false;
	baseStore.secondaryDrawerComponent = null;
});

const openVersions = () => {
	const baseStore = useBaseStore();

	baseStore.secondaryDrawerComponent = shallowRef(PlanVersionsDrawer);
	baseStore.showSecondaryDrawer = true;
};

const load = async (uuid: string) => {
	await store.loadPlan(uuid);
	store.loadResolvedPlan(uuid);
};

onMounted(() => load(plan_uuid));

const close = async () => {
	if (store.hasUnsavedChanges) {
		const confirmed = await useDialog().confirm({
			title: t("base_price.bruto.editor.discard_title"),
			message: t("base_price.bruto.editor.discard_message"),
			confirmButtonText: t("base_price.bruto.editor.discard_button"),
			isDelete: true,
		});

		if (!confirmed) return;
	}

	router.push("/base-prices?pricing_model=bruto");
};

/**
 * Saving writes a new plan and archives the one it came from, so the editor
 * moves to the plan that was created — the route carries the uuid.
 */
const save = async () => {
	const confirmed = await useDialog().confirm({
		title: t("base_price.bruto.editor.save_confirm_title"),
		message: t("base_price.bruto.editor.save_confirm_message", {
			name: store.plan?.name ?? "",
		}),
		confirmButtonText: t("base_price.bruto.editor.save"),
	});

	if (!confirmed) return;

	const saved = await store.savePlan();
	if (!saved) return;

	await router.replace(editPath(saved.uuid));
	await load(saved.uuid);
};
</script>

<template>
	<Teleport to="body">
		<div class="bruto-editor-fullscreen">
			<v-toolbar flat height="60">
				<div class="pl-5">
					<main-breadcrumbs :breadcrumbs="breadcrumbs" inline />
				</div>
				<v-spacer />

				<v-btn
					class="mr-2"
					variant="tonal"
					:loading="store.saving"
					:disabled="!store.hasUnsavedChanges"
					v-tooltip:bottom="t('base_price.bruto.editor.save_tooltip')"
					@click="save"
				>
					{{ t("base_price.bruto.editor.save") }}
				</v-btn>

				<v-btn
					size="40"
					variant="text"
					color="on-surface"
					v-tooltip="t('base_price.bruto.editor.open_versions')"
					@click="openVersions"
				>
					<v-icon size="20" icon="fa: fa-light fa-rectangle-list" />
				</v-btn>

				<v-divider vertical class="mx-2" />

				<v-btn
					size="40"
					variant="text"
					color="on-surface"
					v-tooltip="t('base_price.bruto.editor.close')"
					@click="close"
				>
					<v-icon size="20" icon="fa: fa-regular fa-xmark" />
				</v-btn>
			</v-toolbar>
			<v-divider />

			<div
				v-if="store.loading || (store.resolving && !store.nodes.length)"
				class="d-flex align-center justify-center h-100"
			>
				<v-progress-circular indeterminate color="primary" />
			</div>

			<div v-else class="bruto-editor-body">
				<div class="bruto-editor-sidebar">
					<bruto-hierarchy-sidebar />
				</div>
				<div class="bruto-editor-main">
					<bruto-cpm-panel />
				</div>
			</div>

			<!--
				Rendered here rather than teleported into BaseLayout's drawer, for
				the same reason as in BasePricePlanEditor: a second teleport beside
				this one had Vue reconciling anchors across detached subtrees.
			-->
			<div
				v-show="useBaseStore().showSecondaryDrawer"
				class="editor-drawer-scrim"
				@click="useBaseStore().showSecondaryDrawer = false"
			/>
			<aside
				class="editor-drawer"
				:class="{
					'editor-drawer--open': useBaseStore().showSecondaryDrawer,
				}"
				:style="{ width: `${useBaseStore().secondaryDrawerWidth}px` }"
			>
				<component
					:is="useBaseStore().secondaryDrawerComponent"
					v-if="useBaseStore().secondaryDrawerComponent"
				/>
			</aside>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.bruto-editor-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 1999;
	background: rgb(var(--v-theme-background));
	display: flex;
	flex-direction: column;
}

.bruto-editor-body {
	flex: 1;
	display: flex;
	min-height: 0;
}

.bruto-editor-sidebar {
	flex: 0 0 440px;
	border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	min-height: 0;
	background: rgb(var(--v-theme-surface));
}

.bruto-editor-main {
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.editor-drawer-scrim {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.3);
	z-index: 1;
}

.editor-drawer {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 2;
	max-width: 100%;
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 0 14px rgba(0, 0, 0, 0.16);
	overflow: hidden;
	transform: translateX(100%);
	transition: transform 0.2s ease;
	visibility: hidden;
}

.editor-drawer--open {
	transform: translateX(0);
	visibility: visible;
}
</style>
